import { Box, Paper } from "@mui/material";
import Autocomplete, { AutocompleteProps } from "@mui/material/Autocomplete";
import Stack from "@mui/material/Stack";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import * as Sentry from "@sentry/react";
import { ReactNode } from "react";
import { Controller, FieldValues, Path, PathValue, get, useFormContext } from "react-hook-form";

import { ERROR_BOUNDARY_MESSAGE } from "@/constants";

interface Option {
  name: string;
  value: number | string;
}

export type FormMultiSelectValue = Array<string | number>;

export interface FormMultiSelectProps<TFormValues>
  extends Partial<
    Omit<
      AutocompleteProps<Option, true, false, false>,
      "name" | "multiple" | "disableClearable" | "freeSolo" | "control" | "options" | "defaultValue"
    >
  > {
  name: Path<TFormValues>;
  getOptionSecondaryLabel?: (option: Option) => string;
  getOptionIcon?: (option: Option) => ReactNode;
  getFilteredOptions?: (option: Option[], values: TFormValues) => Option[];
  required?: TextFieldProps["required"];
  label?: TextFieldProps["label"];
  helperText?: TextFieldProps["helperText"];
  options: Option[];
  paperHeader?: ReactNode;
}

export function FormMultiSelect<TFormValues extends FieldValues>({
  name,
  getOptionSecondaryLabel = () => "",
  getOptionIcon = () => null,
  getFilteredOptions = (options) => options,
  label = "",
  options = [],
  required = false,
  disabled = false,
  helperText,
  paperHeader,
  ...props
}: FormMultiSelectProps<TFormValues>) {
  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<TFormValues>();
  const values = watch();
  const error = get(errors, name);

  return (
    <Sentry.ErrorBoundary fallback={<div>{ERROR_BOUNDARY_MESSAGE.component}</div>}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          return (
            <Autocomplete<Option, true, false, false>
              value={options
                .sort((a, b) => a.name.localeCompare(b.name))
                .filter((item) => field.value.includes(item.value))}
              options={getFilteredOptions(options, values)}
              getOptionLabel={(option) => option.name ?? ""}
              disabled={disabled}
              fullWidth
              multiple
              onChange={(event, value) => {
                setValue(
                  field.name,
                  (!value ? [] : value.map((item) => (item ? item.value : null)).filter(Boolean)) as PathValue<
                    TFormValues,
                    Path<TFormValues>
                  >
                );
              }}
              renderInput={(params) => {
                return (
                  <TextField
                    {...params}
                    name={field.name}
                    error={!!error}
                    helperText={error ? error.message : helperText}
                    label={label}
                    required={required} // Only used for the "*" that MUI adds
                    fullWidth
                    slotProps={{
                      htmlInput: {
                        ...params.inputProps,
                        autoComplete: "off",
                      },
                    }}
                  />
                );
              }}
              slots={{
                paper: ({ children, ...props }) => {
                  return (
                    <Paper {...props}>
                      {paperHeader && <Box onMouseDown={(event) => event.preventDefault()}>{paperHeader}</Box>}
                      {children}
                    </Paper>
                  );
                },
              }}
              renderOption={(props, option) => {
                return (
                  <Stack
                    component="li"
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between !important"
                    spacing={2}
                    {...props}
                    key={props.id}
                  >
                    <Stack
                      sx={{ "& svg": { color: "primary.main", fontSize: 20 } }}
                      direction="row"
                      alignItems="center"
                      spacing={2}
                    >
                      {getOptionIcon(option)}
                      <Stack direction="column">
                        <Typography variant="body1">{option.name ?? ""}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {getOptionSecondaryLabel(option)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                );
              }}
              {...props}
            />
          );
        }}
      />
    </Sentry.ErrorBoundary>
  );
}
